import { Controller } from "@hotwired/stimulus"

// The right-hand overlay on the racing surfaces — "How to watch" and
// "Subscriptions" share one shell.
//
// Contents are fetched on open rather than rendered with the page: a discover
// list of 20 events would otherwise resolve 20 full broadcast lists that nobody
// asked for.
//
// Forms inside the drawer are submitted here rather than by Turbo. They are
// settings toggles, and Turbo would follow their redirect and re-render the
// page *behind* the drawer, leaving the panel showing the state from before
// the click — you press "Race only" and nothing appears to happen. Submitting
// in place and re-fetching the body makes the toggle reflect what was saved.
//
// The page behind does go stale (the header's subscription count, the followed
// badges on each row), so a drawer that saved anything refreshes it on close.
//
// The heading comes from the opening control (`data-racing-drawer-label-param`)
// rather than the fetched body, so the panel is correctly labelled while it is
// still loading.
export default class extends Controller {
  static targets = ["scrim", "panel", "body", "title"]

  connect() {
    this.closeOnEscape = this.closeOnEscape.bind(this)
    this.url = null
    this.dirty = false
  }

  disconnect() {
    document.removeEventListener("keydown", this.closeOnEscape)
    document.documentElement.classList.remove("overflow-hidden")
  }

  async open(event) {
    event.preventDefault()
    event.stopPropagation()

    const url = event.params.url
    if (!url) return

    this.setLabel(event.params.label)
    this.show()
    await this.load(url)
  }

  // Re-fetch in place when the country select inside the drawer changes, so the
  // page behind it keeps its scroll position.
  reload(event) {
    if (event.target.value) this.load(event.target.value)
  }

  // Same idea for the session chips: swap the body, keep the drawer open.
  select(event) {
    event.preventDefault()
    this.load(event.params.url)
  }

  // `button_to` renders a real form; we keep the markup and intercept the
  // submit so the panel can update itself. `_method` rides along in the body,
  // so Rails still sees the PATCH/DELETE the form asked for.
  async submitForm(event) {
    const form = event.target.closest("form")
    if (!form || !this.bodyTarget.contains(form)) return

    event.preventDefault()
    form.querySelectorAll("button").forEach((b) => {
      b.disabled = true
    })

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        headers: { Accept: "text/html" },
      })
      if (!response.ok) throw new Error(response.status)
      this.dirty = true
      await this.load(this.url)
    } catch {
      form.querySelectorAll("button").forEach((b) => {
        b.disabled = false
      })
    }
  }

  async load(url) {
    if (!url) return

    this.url = url
    this.bodyTarget.innerHTML =
      '<div class="p-5 text-sm text-app-text-muted">Loading…</div>'

    try {
      const response = await fetch(url, { headers: { Accept: "text/html" } })
      if (!response.ok) throw new Error(response.status)
      this.bodyTarget.innerHTML = await response.text()
    } catch {
      this.bodyTarget.innerHTML =
        '<div class="p-5 text-sm text-app-text-muted">Could not load that. Try again.</div>'
    }
  }

  setLabel(label) {
    if (!label || !this.hasTitleTarget) return

    this.titleTarget.textContent = label
    this.panelTarget.setAttribute("aria-label", label)
  }

  show() {
    this.scrimTarget.classList.remove("hidden")
    this.panelTarget.classList.remove("hidden")
    document.documentElement.classList.add("overflow-hidden")
    document.addEventListener("keydown", this.closeOnEscape)
    this.panelTarget.querySelector("button")?.focus()
  }

  close() {
    this.scrimTarget.classList.add("hidden")
    this.panelTarget.classList.add("hidden")
    document.documentElement.classList.remove("overflow-hidden")
    document.removeEventListener("keydown", this.closeOnEscape)

    if (!this.dirty) return

    // Something was saved, so the list behind is out of date.
    this.dirty = false
    if (window.Turbo) {
      window.Turbo.visit(window.location.href, { action: "replace" })
    } else {
      window.location.reload()
    }
  }

  closeOnEscape(event) {
    if (event.key === "Escape") this.close()
  }
}
